import { useState, useEffect, useCallback } from "react";
import { employeeApi } from "../api/employeeApi";
import hrService from "../api/services/hrService";

export const useEmployees = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Use internal HR service (base URL from API config)
      const dropdownResult = await hrService.getEmployeesForDropdown();
      let employeeData = Array.isArray(dropdownResult?.data)
        ? dropdownResult.data
        : [];

      // Transform HR Dropdown API data to match expected structure

      if (employeeData.length > 0) {
        employeeData = employeeData.map((emp) => {
          // Extract employeeId, name, email, department from API response
          const employeeId = emp.employeeId || emp.id || emp._id || "";
          const firstName = emp.firstName || emp.name?.split(' ')[0] || emp.personalInfo?.firstName || emp.user?.firstName || "";
          const lastName = emp.lastName || emp.name?.split(' ').slice(1).join(' ') || emp.personalInfo?.lastName || emp.user?.lastName || "";
          const name = emp.name || `${firstName} ${lastName}`.trim() || "";
          const fullName = name;
          const email = emp.email || emp.contactInfo?.personalEmail || emp.user?.email || "";
          const department = emp.department || emp.employmentInfo?.department || "";
          const role = emp.role || emp.user?.role || "employee";
          return {
            id: employeeId,
            _id: employeeId,
            employeeId,
            firstName,
            lastName,
            name,
            fullName,
            role,
            department,
            email,
            personalInfo: { firstName, lastName },
            employmentInfo: { position: emp.employmentInfo?.position || "Employee", department },
          };
        });
      } else {
      }

      // If no data from API, provide mock data for development
      if (employeeData.length === 0) {
        employeeData = [
          {
            id: "emp1",
            _id: "emp1",
            firstName: "John",
            lastName: "Doe",
            name: "John Doe",
            fullName: "John Doe",
            role: "employee",
            department: "Engineering",
            email: "john.doe@company.com",
            personalInfo: {
              firstName: "John",
              lastName: "Doe",
            },
            employmentInfo: {
              position: "Software Engineer",
              department: "Engineering",
            },
          },
          {
            id: "emp2",
            _id: "emp2",
            firstName: "Jane",
            lastName: "Smith",
            name: "Jane Smith",
            fullName: "Jane Smith",
            role: "manager",
            department: "HR",
            email: "jane.smith@company.com",
            personalInfo: {
              firstName: "Jane",
              lastName: "Smith",
            },
            employmentInfo: {
              position: "HR Manager",
              department: "HR",
            },
          },
          {
            id: "emp3",
            _id: "emp3",
            firstName: "Mike",
            lastName: "Johnson",
            name: "Mike Johnson",
            fullName: "Mike Johnson",
            role: "sales_manager",
            department: "Sales",
            email: "mike.johnson@company.com",
            personalInfo: {
              firstName: "Mike",
              lastName: "Johnson",
            },
            employmentInfo: {
              position: "Sales Manager",
              department: "Sales",
            },
          },
        ];
      }

      setEmployees(employeeData);
    } catch (err) {
      setError(err.message || "Failed to fetch employees");

      // Provide mock data on error for development
      const mockEmployees = [
        {
          id: "emp1",
          _id: "emp1",
          firstName: "John",
          lastName: "Doe",
          name: "John Doe",
          role: "employee",
          department: "Engineering",
          email: "john.doe@company.com",
          personalInfo: {
            firstName: "John",
            lastName: "Doe",
          },
          employmentInfo: {
            position: "Software Engineer",
            department: "Engineering",
          },
        },
        {
          id: "emp2",
          _id: "emp2",
          firstName: "Jane",
          lastName: "Smith",
          name: "Jane Smith",
          role: "manager",
          department: "HR",
          email: "jane.smith@company.com",
          personalInfo: {
            firstName: "Jane",
            lastName: "Smith",
          },
          employmentInfo: {
            position: "HR Manager",
            department: "HR",
          },
        },
        {
          id: "emp3",
          _id: "emp3",
          firstName: "Mike",
          lastName: "Johnson",
          name: "Mike Johnson",
          role: "sales_manager",
          department: "Sales",
          email: "mike.johnson@company.com",
          personalInfo: {
            firstName: "Mike",
            lastName: "Johnson",
          },
          employmentInfo: {
            position: "Sales Manager",
            department: "Sales",
          },
        },
      ];
      setEmployees(mockEmployees);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch employees on component mount
  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const getEmployeeById = useCallback(async (employeeId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await employeeApi.getEmployeeById(employeeId);
      return response.data;
    } catch (err) {
      setError(err.message || "Failed to fetch employee");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const createEmployee = useCallback(async (employeeData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await employeeApi.createEmployee(employeeData);
      setEmployees((prev) => [...prev, response.data]);
      return response.data;
    } catch (err) {
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateEmployee = useCallback(async (employeeId, employeeData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await employeeApi.updateEmployee(
        employeeId,
        employeeData
      );
      setEmployees((prev) =>
        prev.map((employee) =>
          employee.id === employeeId ? response.data : employee
        )
      );
      return response.data;
    } catch (err) {
      setError(err.message || "Failed to update employee");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteEmployee = useCallback(async (employeeId) => {
    try {
      setLoading(true);
      setError(null);
      await employeeApi.deleteEmployee(employeeId);
      setEmployees((prev) =>
        prev.filter((employee) => employee.id !== employeeId)
      );
    } catch (err) {
      setError(err.message || "Failed to delete employee");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const searchEmployees = useCallback(async (searchTerm) => {
    try {
      setLoading(true);
      setError(null);
      const response = await employeeApi.searchEmployees(searchTerm);
      return response.data || [];
    } catch (err) {
      setError(err.message || "Failed to search employees");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getEmployeesByDepartment = useCallback(async (departmentId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await employeeApi.getEmployeesByDepartment(departmentId);
      return response.data || [];
    } catch (err) {
      setError(err.message || "Failed to fetch employees by department");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    employees,
    loading,
    error,
    fetchEmployees,
    getEmployeeById,
    createEmployee,
    updateEmployee,
    deleteEmployee,
    searchEmployees,
    getEmployeesByDepartment,
    // React Query compatibility
    refetch: fetchEmployees,
    data: employees,
    isLoading: loading,
    isError: !!error,
  };
};

// Export individual hooks for direct import
export const useEmployeesForDropdown = () => {
  const { employees, loading, error } = useEmployees();


  // Ensure employees is an array and handle loading/error states
  const safeEmployees = Array.isArray(employees) ? employees : [];

  const dropdownOptions = safeEmployees.map((emp) => {
    const label = `${emp.firstName || emp.name || "Unknown"} ${emp.lastName || ""
      }`.trim();
    const value = emp.id || emp._id;
    return {
      value,
      label,
      ...emp,
    };
  });


  const result = {
    employees: dropdownOptions,
    loading,
    error,
    originalEmployees: safeEmployees,
    // React Query compatibility
    data: dropdownOptions,
    isLoading: loading,
    isError: !!error,
  };

  return result;
};

export const useManagersForDropdown = () => {
  const { employees, loading, error } = useEmployees();


  // Ensure employees is an array and handle loading/error states
  const safeEmployees = Array.isArray(employees) ? employees : [];

  const managers = safeEmployees.filter(
    (emp) =>
      emp.role === "manager" ||
      emp.role === "super_admin" ||
      emp.role === "hr_manager" ||
      emp.role === "sales_manager"
  );


  const dropdownOptions = managers.map((emp) => {
    const label = `${emp.firstName || emp.name || "Unknown"} ${emp.lastName || ""
      }`.trim();
    const value = emp.id || emp._id;
    return {
      value,
      label,
      ...emp,
    };
  });


  const result = {
    managers: dropdownOptions,
    loading,
    error,
    originalManagers: managers,
    // React Query compatibility
    data: dropdownOptions,
    isLoading: loading,
    isError: !!error,
  };

  return result;
};
